import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getWorkOrderDetail } from "@/lib/inventory-catalog.functions";
import {
  allocateWorkOrder, startWorkOrderPicking, pickWorkOrderAllocation,
  startWorkOrderAssembly, completeWorkOrder,
} from "@/lib/inventory.functions";
import { runServerFn } from "@/lib/server-fn";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { ArrowLeft, Loader2, Plus, Trash2, Check } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/inventory/kits/$workOrderId")({
  component: WorkOrderDetailPage,
});

type AllocDraft = { component_sku_id: string; source_location_id: string; qty: number };

function WorkOrderDetailPage() {
  const { workOrderId } = Route.useParams();
  const qc = useQueryClient();
  const getDetail = useServerFn(getWorkOrderDetail);
  const allocateFn = useServerFn(allocateWorkOrder);
  const startPickingFn = useServerFn(startWorkOrderPicking);
  const pickAllocFn = useServerFn(pickWorkOrderAllocation);
  const startAssemblyFn = useServerFn(startWorkOrderAssembly);
  const completeFn = useServerFn(completeWorkOrder);

  const { data, isLoading } = useQuery({
    queryKey: ["work-order", workOrderId],
    queryFn: () => getDetail({ data: { workOrderId } }),
  });

  // Local draft allocations keyed by a row index (only used when status === 'draft')
  const [drafts, setDrafts] = useState<AllocDraft[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Initialize drafts from existing allocations once
  const initKey = data ? `${data.workOrder.id}:${data.allocations.length}` : "";
  useMemo(() => {
    if (!data) return;
    if (data.workOrder.status === "draft" && drafts.length === 0 && data.allocations.length > 0) {
      setDrafts(
        data.allocations.map((a) => ({
          component_sku_id: a.component_sku_id,
          source_location_id: a.source_location_id,
          qty: a.qty_required,
        })),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initKey]);

  if (isLoading || !data) {
    return <div className="p-6 flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }

  const wo = data.workOrder;
  const status = wo.status as string;
  const skuById = new Map(data.skus.map((s) => [s.id, s] as const));
  const locById = new Map(data.locations.map((l) => [l.id, l] as const));

  // Availability summed by sku → list of {location_id, qty}
  const availBySku = new Map<string, { location_id: string; qty: number }[]>();
  for (const r of data.availability) {
    const list = availBySku.get(r.sku_id) ?? [];
    list.push({ location_id: r.location_id, qty: r.qty_on_hand });
    availBySku.set(r.sku_id, list);
  }

  const isEditable = status === "draft";

  const addDraft = (sku_id: string) => {
    const opts = availBySku.get(sku_id) ?? [];
    const first = opts[0];
    setDrafts((d) => [...d, {
      component_sku_id: sku_id,
      source_location_id: first?.location_id ?? "",
      qty: 0,
    }]);
  };

  const removeDraft = (idx: number) => setDrafts((d) => d.filter((_, i) => i !== idx));
  const updateDraft = (idx: number, patch: Partial<AllocDraft>) =>
    setDrafts((d) => d.map((x, i) => (i === idx ? { ...x, ...patch } : x)));

  const draftTotalsBySku = new Map<string, number>();
  for (const d of drafts) {
    draftTotalsBySku.set(d.component_sku_id, (draftTotalsBySku.get(d.component_sku_id) ?? 0) + (d.qty || 0));
  }

  const allReqsMet = data.requirements.every((r) => (draftTotalsBySku.get(r.component_sku_id) ?? 0) === r.total_qty);

  // Per-bin on-hand for clamping. A draft row's qty can never exceed what's on
  // hand at the chosen bin, minus any other draft rows already pulling from
  // that same bin for the same component.
  const onHandKey = (sku: string, loc: string) => `${sku}::${loc}`;
  const onHandByBin = new Map<string, number>();
  for (const r of data.availability) {
    onHandByBin.set(onHandKey(r.sku_id, r.location_id), r.qty_on_hand);
  }
  const otherAllocByBin = (idx: number, sku: string, loc: string) =>
    drafts.reduce((sum, d, i) => (i !== idx && d.component_sku_id === sku && d.source_location_id === loc ? sum + (d.qty || 0) : sum), 0);
  const maxForRow = (idx: number, d: AllocDraft) => {
    if (!d.source_location_id) return 0;
    const onHand = onHandByBin.get(onHandKey(d.component_sku_id, d.source_location_id)) ?? 0;
    return Math.max(0, onHand - otherAllocByBin(idx, d.component_sku_id, d.source_location_id));
  };
  const draftsValid = drafts.every((d, i) =>
    !!d.component_sku_id && !!d.source_location_id && d.qty > 0 && d.qty <= maxForRow(i, d),
  );

  const saveAllocate = async () => {
    if (!draftsValid) return toast.error("Every row needs a source bin and qty");
    if (!allReqsMet) return toast.error("Allocated quantities must match component requirements");
    setSubmitting(true);
    try {
      await runServerFn(() => allocateFn({ data: { workOrderId, allocations: drafts } }), "Allocate work order");
      toast.success("Work order allocated");
      qc.invalidateQueries({ queryKey: ["work-order", workOrderId] });
      qc.invalidateQueries({ queryKey: ["work-orders"] });
    } catch { /* toast handled */ } finally { setSubmitting(false); }
  };

  const transition = async (fn: () => Promise<unknown>, label: string) => {
    try {
      await runServerFn(fn, label);
      toast.success(label + " ✓");
      qc.invalidateQueries({ queryKey: ["work-order", workOrderId] });
      qc.invalidateQueries({ queryKey: ["work-orders"] });
    } catch { /* toast handled */ }
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <Link to="/app/inventory/kits" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to kits
          </Link>
          <h2 className="text-lg font-semibold">
            Build {wo.qty} × <span className="font-mono">{wo.skus?.sku_code}</span> <span className="text-muted-foreground">{wo.skus?.name}</span>
          </h2>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <StatusPill tone={statusTone(status)}>{status}</StatusPill>
            <span>Assemble at <span className="font-mono">{data.destination?.code ?? "—"}</span></span>
            <span>Due {wo.expected_completion_date ?? "—"}</span>
          </div>
        </div>
        <WorkflowActions
          status={status}
          canAllocate={drafts.length > 0 && allReqsMet && draftsValid}
          submitting={submitting}
          onAllocate={saveAllocate}
          onStartPicking={() => transition(() => startPickingFn({ data: { workOrderId } }), "Start picking")}
          onStartAssembly={() => transition(() => startAssemblyFn({ data: { workOrderId } }), "Start assembly")}
          onComplete={() => transition(() => completeFn({ data: { workOrderId } }), "Work complete")}
        />
      </div>

      {/* Requirements card */}
      <div className="rounded-md border">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="font-medium">Components needed</h3>
          <Badge variant="outline">{data.requirements.length} SKUs</Badge>
        </div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Component</TableHead>
            <TableHead className="text-right">Required</TableHead>
            <TableHead>Available</TableHead>
            {isEditable && <TableHead className="text-right">Allocated</TableHead>}
          </TableRow></TableHeader>
          <TableBody>
            {data.requirements.map((r) => {
              const sku = skuById.get(r.component_sku_id);
              const opts = availBySku.get(r.component_sku_id) ?? [];
              const totalAvail = opts.reduce((a, b) => a + b.qty, 0);
              const allocated = draftTotalsBySku.get(r.component_sku_id) ?? 0;
              const short = totalAvail < r.total_qty;
              const allocShort = isEditable && allocated !== r.total_qty;
              return (
                <TableRow key={r.component_sku_id}>
                  <TableCell>
                    <div className="font-mono text-sm">{sku?.sku_code}</div>
                    <div className="text-xs text-muted-foreground">{sku?.name}</div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{r.total_qty}</TableCell>
                  <TableCell className="text-sm">
                    <span className={short ? "text-destructive font-medium" : ""}>{totalAvail}</span>
                    {opts.length > 0 && (
                      <span className="text-xs text-muted-foreground ml-2">
                        in {opts.length} bin{opts.length === 1 ? "" : "s"}: {opts.slice(0, 3).map((o) => `${locById.get(o.location_id)?.code ?? "?"}(${o.qty})`).join(", ")}
                        {opts.length > 3 && "…"}
                      </span>
                    )}
                  </TableCell>
                  {isEditable && (
                    <TableCell className="text-right">
                      <span className={"tabular-nums " + (allocShort ? "text-amber-600 font-medium" : "text-emerald-600")}>{allocated} / {r.total_qty}</span>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Allocation editor (draft only) */}
      {isEditable && (
        <div className="rounded-md border">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div>
              <h3 className="font-medium">Allocation</h3>
              <p className="text-xs text-muted-foreground">Pick which bins to pull from. Split a component across multiple bins if needed.</p>
            </div>
          </div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Component</TableHead>
              <TableHead>Source bin</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {drafts.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">No allocations yet. Add rows below.</TableCell></TableRow>
              )}
              {drafts.map((d, idx) => {
                const sku = skuById.get(d.component_sku_id);
                const opts = availBySku.get(d.component_sku_id) ?? [];
                const rowMax = maxForRow(idx, d);
                const overMax = d.qty > rowMax;
                return (
                  <TableRow key={idx}>
                    <TableCell>
                      <div className="font-mono text-sm">{sku?.sku_code}</div>
                      <div className="text-xs text-muted-foreground">{sku?.name}</div>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={d.source_location_id}
                        onValueChange={(v) => {
                          // When swapping bins, also clamp qty to the new bin's available headroom.
                          const onHand = onHandByBin.get(onHandKey(d.component_sku_id, v)) ?? 0;
                          const headroom = Math.max(0, onHand - otherAllocByBin(idx, d.component_sku_id, v));
                          updateDraft(idx, { source_location_id: v, qty: Math.min(d.qty || 0, headroom) });
                        }}
                      >
                        <SelectTrigger className="h-8 w-48"><SelectValue placeholder="Pick bin" /></SelectTrigger>
                        <SelectContent>
                          {opts.length === 0 && <SelectItem value="__none" disabled>No stock available</SelectItem>}
                          {opts.map((o) => (
                            <SelectItem key={o.location_id} value={o.location_id}>
                              {locById.get(o.location_id)?.code ?? o.location_id} ({o.qty} available)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-col items-end">
                        <Input
                          type="number"
                          min={1}
                          max={rowMax}
                          className={"h-8 w-24 text-right " + (overMax ? "border-destructive" : "")}
                          value={d.qty || ""}
                          onChange={(e) => {
                            const raw = parseInt(e.target.value || "0", 10) || 0;
                            const clamped = Math.min(Math.max(0, raw), rowMax);
                            updateDraft(idx, { qty: clamped });
                          }}
                        />
                        <span className="text-[10px] text-muted-foreground mt-0.5">max {rowMax}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button size="icon" variant="ghost" onClick={() => removeDraft(idx)}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <div className="p-3 flex flex-wrap gap-2 border-t">
            {data.requirements.map((r) => {
              const sku = skuById.get(r.component_sku_id);
              return (
                <Button key={r.component_sku_id} size="sm" variant="outline" onClick={() => addDraft(r.component_sku_id)}>
                  <Plus className="h-3 w-3 mr-1" />Add row · <span className="font-mono ml-1">{sku?.sku_code}</span>
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {/* Picking view */}
      {(status === "allocated" || status === "picking" || status === "picked" || status === "assembly" || status === "completed") && (
        <div className="rounded-md border">
          <div className="px-4 py-3 border-b">
            <h3 className="font-medium">Pick list</h3>
          </div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Component</TableHead>
              <TableHead>From bin</TableHead>
              <TableHead className="text-right">Qty</TableHead>
              <TableHead className="text-right">Status</TableHead>
              <TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.allocations.map((a) => {
                const sku = skuById.get(a.component_sku_id);
                const done = a.qty_picked >= a.qty_required;
                return (
                  <TableRow key={a.id}>
                    <TableCell>
                      <div className="font-mono text-sm">{sku?.sku_code}</div>
                      <div className="text-xs text-muted-foreground">{sku?.name}</div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{locById.get(a.source_location_id)?.code ?? "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{a.qty_required}</TableCell>
                    <TableCell className="text-right">
                      {done ? <Badge variant="outline" className="text-emerald-600"><Check className="h-3 w-3 mr-1" />Picked</Badge>
                        : <Badge variant="outline">Open</Badge>}
                    </TableCell>
                    <TableCell className="text-right">
                      {status === "picking" && !done && (
                        <Button size="sm" onClick={() => transition(() => pickAllocFn({ data: { allocationId: a.id } }), "Pick")}>Mark picked</Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {status === "completed" && (
        <div className="rounded-md border bg-emerald-500/5 p-4 text-sm">
          <span className="font-medium text-emerald-600">Work complete.</span>{" "}
          {wo.qty} × <span className="font-mono">{wo.skus?.sku_code}</span> assembled at <span className="font-mono">{data.destination?.code}</span>.
          A putaway task has been created — the system suggests a storage destination but operators can put them away anywhere.
        </div>
      )}
    </div>
  );
}

function WorkflowActions({
  status, submitting, canAllocate,
  onAllocate, onStartPicking, onStartAssembly, onComplete,
}: {
  status: string; submitting: boolean; canAllocate: boolean;
  onAllocate: () => void; onStartPicking: () => void;
  onStartAssembly: () => void; onComplete: () => void;
}) {
  if (status === "draft") return (
    <Button onClick={onAllocate} disabled={!canAllocate || submitting}>
      {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
      Save allocation
    </Button>
  );
  if (status === "allocated") return <Button onClick={onStartPicking}>Start picking</Button>;
  if (status === "picking") return <Button disabled variant="outline">Picking in progress</Button>;
  if (status === "picked") return <Button onClick={onStartAssembly}>Start assembly</Button>;
  if (status === "assembly") return <Button onClick={onComplete}>Work complete</Button>;
  return null;
}